/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCreateGroupMutation } from "~/generated/graphql";
import { showError } from "~/services/toast";
import { isNotEmpty } from "~/tools";
import { ResourceGroupData } from "../../ResourceGroupCreate/components/ResourceGroupCreateWizard/constants";

type Props = {
  onSuccess: (id: string) => void;
};

export function useCreateResourceGroup({ onSuccess }: Props) {
  const [createGroupMutation, { loading }] = useCreateGroupMutation({
    onCompleted: (data) => {
      if (data.createGroup) {
        onSuccess(data?.createGroup.id);
      }
    },
    onError: (error) => {
      showError(error.message);
    },
  });

  async function createGroup(
    data: ResourceGroupData
  ): Promise<{ id: string; name: string } | undefined> {
    const isExpression = isNotEmpty(data.expression ?? "");

    const prepareExpression = [
      {
        order: 1,
        tagsExpression: data.expression,
        resourceId: null,
      },
    ];

    const prepareResources = data.manual_selected_resources?.map(
      (resourceId: string, i: number) => {
        return {
          order: i + 1,
          tagsExpression: null,
          resourceId,
        };
      }
    ) ?? [
      {
        order: 1,
        tagsExpression: null,
        resourceId: "",
      },
    ];

    const resourceSelectors = isExpression
      ? [...prepareResources, ...prepareExpression]
      : prepareResources;

    const prepareGroup = {
      description: data?.details?.description ?? "",
      name: data?.details?.name ?? "",
      metadata: data?.details?.metadata,
      resourceSelectors,
    };

    const result = await createGroupMutation({
      variables: {
        input: prepareGroup,
      },
    });

    if (result.data?.createGroup) {
      onSuccess(result.data.createGroup.id);
    }
    return result.data?.createGroup;
  }

  return {
    createGroup,
    loading,
  };
}
